import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import {
  Loader2, Banknote, Smartphone, Store, Check, Search, AlertTriangle, Undo2, XCircle,
  FileText, ScanLine, X, UserSearch, Hash,
} from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { btn } from '../../utils/buttonStyles';
import { paymentsAPI } from '../services/PaymentApi';
import DeskScanModal from './DeskScanModal';

/**
 * The front desk: settling up with the person standing at the counter.
 *
 * ── IT STARTS FROM A PERSON, NOT FROM A LIST ────────────────────────────────
 *
 * This screen used to open the academy's whole order book — hundreds of rows,
 * headed by the academy's outstanding balance — and leave the receptionist to
 * scroll. That is a finance report wearing a desk's clothes. The desk has one
 * job: someone is in front of you, find their money and settle it. So:
 *
 *   · Nothing loads until a player is chosen. An empty screen that asks a
 *     question beats a full one that answers the wrong one.
 *   · The search is the interface. Name, email or phone, and every token has to
 *     match something — so "smith 4821" finds the Smith on that number.
 *   · Or scan their profile QR, which is the same lookup by id.
 *   · No academy totals. Whether the venue is owed $219 across ten people is a
 *     report; it tells the person at the counter nothing about the person at the
 *     counter. What IS shown is what THIS player owes.
 *   · Bounded by date, widened on request. A player's history is not a thing to
 *     load in full on the chance somebody asks about 2019.
 *
 * ── TWO NAMES, ONE PAYMENT ──────────────────────────────────────────────────
 *
 * A parent pays for a child, so the name the desk is given is often not the name
 * on the order. Both sides are searchable and both sides match: searching the
 * child finds the parent's order, with the parent named as the payer. Same
 * reason the search results carry "managed by" — a child usually has no email,
 * so whose child they are is the only thing separating two of the same name.
 *
 * ── WHY IT ASKS TWICE ───────────────────────────────────────────────────────
 *
 * Confirming is irreversible in practice: it books income, moves the academy's
 * reported revenue and is what a payout is later computed from. So the method is
 * chosen, and then confirmed, on the row itself. No dialog — a receptionist with
 * a queue should not be navigating, and a dialog over a list is the thing that
 * gets dismissed by reflex.
 *
 * Who may confirm is the server's business — `manage_programs` on the academy,
 * checked on the search, the listing and the confirmation alike. This screen
 * never decides that for itself; it surfaces the refusal, because "you don't
 * have permission" is a better message than a control that silently isn't there.
 *
 * ── THE LAYOUT DOES NOT MOVE ────────────────────────────────────────────────
 *
 * Controls keep their place and their size whatever the state: filters never
 * reach over and change each other, they disable rather than disappear, rows
 * share one fixed column grid, results overlay instead of pushing the page down,
 * and reloading dims the list instead of replacing it. A counter with somebody
 * waiting is no place for a moving target.
 */

const money = (n, currency = 'USD') => {
  const v = Number(n);
  if (!Number.isFinite(v)) return '—';
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(v);
  } catch { return `${currency} ${v.toFixed(2)}`; }
};

/**
 * What the desk can take. `stripe` is absent on purpose — it is not something
 * anyone records by hand, it is what the payer's own card payment writes.
 */
const METHODS = [
  { id: 'cash',  icon: Banknote,   label: 'Cash' },
  { id: 'venmo', icon: Smartphone, label: 'Venmo' },
  { id: 'card',  icon: Store,      label: 'Card at desk' },
  // A check with no number cannot be chased when it bounces, so the server
  // refuses one and the field below is required rather than optional.
  { id: 'check', icon: FileText,   label: 'Check', needsRef: true, refLabel: 'Check number' },
  { id: 'other', icon: Store,      label: 'Other', refLabel: 'Reference (optional)' },
];

const METHOD = Object.fromEntries(METHODS.map(m => [m.id, m]));

/**
 * How every payment type reads, including the ones the desk cannot create.
 *
 * `stripe` is written by the card flow and shows as "Card (online)" — a venue
 * reconciling a till needs to tell money that arrived at the counter from money
 * that arrived in the platform's account, and "Card" alone hides that.
 */
const METHOD_LABEL = {
  cash:   'Cash',
  venmo:  'Venmo',
  card:   'Card at desk',
  stripe: 'Card (online)',
  check:  'Check',
  other:  'Other',
};

const methodLabel = (m) => METHOD_LABEL[m] || m || 'Not recorded';

/** Local date as YYYY-MM-DD, which is what the API's window takes. */
const isoDay = (d) => {
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

const daysBack = (days) => {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return isoDay(d);
};

/**
 * The window, as shortcuts onto the same two dates.
 *
 * Deliberately NOT a mode that hides the dates: the shortcuts write into `from`
 * and `to`, and which one looks selected is derived by comparing against them.
 * So the screen can never claim "Last 90 days" while the inputs say the 1st to
 * the 2nd, and a shortcut never has to reach over and edit another control —
 * both are views of one piece of state.
 *
 * "Today" is a single day, which is why `to` has to include the day it names;
 * as an exclusive bound it returned nothing at all.
 */
const SHORTCUTS = [
  { id: 'today', label: 'Today',      range: () => ({ from: isoDay(new Date()), to: isoDay(new Date()) }) },
  { id: '7d',    label: '7 days',     range: () => ({ from: daysBack(7),   to: '' }) },
  { id: '90d',   label: '90 days',    range: () => ({ from: daysBack(90),  to: '' }) },
  { id: '12m',   label: '12 months',  range: () => ({ from: daysBack(365), to: '' }) },
  { id: 'all',   label: 'Everything', range: () => ({ from: '', to: '' }) },
];

/**
 * What was bought. The same codes that appear in the order number, so a desk
 * reading US-KSA-TRN-2608230001 off a receipt already knows which option to pick.
 */
const OFFERINGS = [
  { id: 'all', label: 'All offerings' },
  { id: 'OPN', label: 'Open play' },
  { id: 'PRG', label: 'Programmes' },
  { id: 'TRN', label: 'Tournaments' },
  { id: 'MEM', label: 'Memberships' },
  { id: 'CRT', label: 'Court bookings' },
];

/** Narrowing one person's records, not browsing the academy's. */
const STATUSES = [
  { id: 'all',       label: 'All records', status: 'all' },
  { id: 'pending',   label: 'Owed',        status: 'pending' },
  { id: 'paid',      label: 'Settled',     status: 'paid' },
  { id: 'notbooked', label: 'Not booked',  status: 'paid', unmatchedOnly: true },
  { id: 'refunded',  label: 'Refunded',    status: 'refunded' },
  // The mirror of Not booked: there, money with no place; here, a place with no
  // money. Both are somebody having to decide something, which is what this
  // screen is for.
  { id: 'orphaned',  label: 'Still enrolled, charge pulled', status: 'cancelled', orphanedOnly: true },
];

const STATUS = Object.fromEntries(STATUSES.map(s => [s.id, s]));

const when = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
    + ' · ' + d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
};

/**
 * The column geometry, in one place and used by both the header and every row.
 *
 * Shared literally rather than repeated, because a header that drifts from its
 * rows is worse than no header: it labels the wrong column. Below `md` the grid
 * collapses to one column and the labels come back inline — a phone at a counter
 * is a different screen, not a squeezed version of this one.
 */
const COLS = 'md:grid md:grid-cols-[minmax(0,1fr)_8rem_7rem_11rem] md:gap-4 md:items-start';

const PaymentDeskSettings = ({ academyId }) => {
  const { theme } = useTheme();

  // ── Who we are settling with ───────────────────────────────────────────────
  const [term, setTerm]         = useState('');
  const [matches, setMatches]   = useState(null);   // null = not searched yet
  const [seeking, setSeeking]   = useState(false);
  // Set when the term was an order number: the results are then "the payer of
  // that order" rather than "people called this".
  const [matchedOrder, setMatchedOrder] = useState(null);
  // Which result Enter will take. Zero, so typing a name and pressing Enter
  // picks the top match without anybody reaching for the mouse — the desk has a
  // queue, and the top match is right nearly every time.
  const [active, setActive] = useState(0);
  // The receipt in somebody's hand, typed straight in. Its own field rather than
  // sharing the name box: the two are different tasks, one is digits and the
  // other is letters, and a single box that guesses which you meant guesses
  // wrong on somebody called "Aarav 2".
  const [ref, setRef]             = useState('');
  const [refBusy, setRefBusy]     = useState(false);
  const [refMiss, setRefMiss]     = useState(false);
  // The one order a reference asked for. An order number is a precision
  // question, and answering it with the payer's whole history — 19 rows, the
  // wanted one somewhere among them — is not an answer. So the list narrows to
  // it, with one click to see everything else they have.
  const [onlyOrder, setOnlyOrder] = useState(null);
  const [person, setPerson]     = useState(null);
  const [scanning, setScanning] = useState(false);

  // ── Their records ──────────────────────────────────────────────────────────
  const [statusId, setStatusId] = useState('all');
  const [kind, setKind]         = useState('all');
  // One window, two dates. Twelve months to start: it covers the season somebody
  // is actually being asked about without loading a history nobody wants.
  const [from, setFrom] = useState(() => daysBack(365));
  const [to, setTo]     = useState('');
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(false);

  // One row at a time can be open, for one of three actions. Kept as a pair
  // rather than three booleans so opening a second panel closes the first — a
  // row showing both "take payment" and "refund" expanded is a row inviting the
  // wrong click.
  const [panel, setPanel]         = useState(null);   // {id, action}
  const [method, setMethod]       = useState('cash');
  const [reference, setReference] = useState('');     // check number, Venmo id, auth code
  const [reason, setReason]       = useState('');
  const [busy, setBusy]           = useState(null);

  const closePanel = () => { setPanel(null); setReason(''); setReference(''); };
  const openPanel = (id, action) => {
    setPanel({ id, action });
    setReason('');
    setReference('');
    if (action === 'settle') setMethod('cash');
  };

  /**
   * Look people up as they type, and never let an older reply win.
   *
   * Typing "sha" then "sharma" fires two requests and the first can land second,
   * which would replace the right answers with the vaguer ones and read as the
   * search going backwards. The sequence number makes that impossible.
   */
  const seq = useRef(0);
  useEffect(() => {
    const q = term.trim();
    if (q.length < 2) { setMatches(null); setMatchedOrder(null); setSeeking(false); return undefined; }

    const mine = ++seq.current;
    setSeeking(true);
    const t = setTimeout(async () => {
      try {
        const res = await paymentsAPI.academyPayers(academyId, { q });
        if (mine === seq.current) {
          setMatches(res?.people || []);
          setMatchedOrder(res?.matchedOrder || null);
          setActive(0);          // new results, so the selection starts at the top
        }
      } catch (err) {
        if (mine === seq.current) { setMatches([]); toast.error(err?.message || 'Search failed'); }
      } finally {
        if (mine === seq.current) setSeeking(false);
      }
    }, 250);
    // Clearing the spinner as well as the timer.
    //
    // `seeking` is set the moment a search is queued, but the request only goes
    // out 250ms later. If this cleanup ran in between — a re-render, a remount,
    // React's double-invoked effects in development — the timer died and the box
    // was left spinning over a request that had never been made. It looked like
    // a slow search and was actually no search at all.
    return () => { clearTimeout(t); setSeeking(false); };
  }, [term, academyId]);

  /**
   * Jump to an order by its number.
   *
   * Selects whoever paid it and then WIDENS the view to everything: a number
   * quoted off a receipt is usually an old one, and finding the payer only to
   * show them the last twelve months would answer "no records" for the very
   * order that was typed in. The list then narrows to that one order — landing
   * somewhere in a list of forty is not the same as being shown the thing.
   */
  const findByRef = useCallback(async (typed) => {
    const q = typed.trim();
    if (!q) return;
    setRefBusy(true);
    setRefMiss(false);
    try {
      const res = await paymentsAPI.academyPayers(academyId, { q });
      const found = res?.people?.[0];
      if (!found || !res?.matchedOrder) { setRefMiss(true); return; }
      setPerson(found);
      // Widened deliberately: a number off a receipt is usually an old one, so
      // the twelve-month default would hide the very order that was typed in.
      setStatusId('all');
      setKind('all');
      setFrom('');
      setTo('');
      setOnlyOrder(res.matchedOrder);
      setTerm('');
      setMatches(null);
      setMatchedOrder(null);
      closePanel();
    } catch (err) {
      toast.error(err?.message || 'Could not look that up');
    } finally {
      setRefBusy(false);
    }
  }, [academyId]);

  /**
   * A scan is either a person or an order, and each goes where it belongs: a
   * profile code selects that player, a receipt code opens that one order.
   */
  const onScan = useCallback(async (payload) => {
    setScanning(false);
    if (payload.type === 'order') { findByRef(payload.number); return; }
    const playerId = payload.id;
    try {
      const res = await paymentsAPI.academyPayers(academyId, { userId: playerId });
      const found = res?.people?.[0];
      if (!found) {
        toast.error('That player has no payment records at this academy');
        return;
      }
      setTerm('');
      setMatches(null);
      setPerson(found);
    } catch (err) {
      toast.error(err?.message || 'Could not read that code');
    }
  }, [academyId, findByRef]);

  const choose = (p) => {
    setPerson(p);
    setOnlyOrder(null);
    setRef('');
    setTerm('');
    setMatches(null);
    setMatchedOrder(null);
    closePanel();
  };

  const clearPerson = () => {
    setKind('all');
    setRef('');
    setRefMiss(false);
    setOnlyOrder(null);
    setPerson(null);
    setData(null);
    setStatusId('all');
    setFrom(daysBack(365));
    setTo('');
    closePanel();
  };

  /** Which row is mid-action, so a second click cannot double it. */
  const [busyId, setBusyId] = useState(null);

  /**
   * The two ways out of "still enrolled, charge pulled".
   *
   * Both reload afterwards rather than patching the row in place: each changes
   * something the row does not own — a new order on one side, a place given up
   * on the other — and a screen guessing at the result of a money action is how
   * it comes to disagree with the books.
   */
  const rebill = async (order) => {
    setBusyId(order.id);
    try {
      const res = await paymentsAPI.rebillOrphanedEntry(order.id);
      toast.success(res?.orderIds?.length > 1
        ? `${res.orderIds.length} charges raised — they can be paid now.`
        : 'Charge raised — it can be paid now.');
      await load();
    } catch (err) {
      toast.error(err?.message || 'Could not raise the charge');
    } finally {
      setBusyId(null);
    }
  };

  const releaseEntry = async (order) => {
    // One order can hold several entries; each is released by naming it.
    const entries = order.orphaned?.entries || [];
    setBusyId(order.id);
    try {
      for (const e of entries) {
        // eslint-disable-next-line no-await-in-loop
        await paymentsAPI.releaseOrphanedEntry(order.id, {
          subjectType: e.subjectType, subjectId: String(e.subjectId), playerId: e.playerId || null,
        });
      }
      toast.success(entries.length > 1 ? 'Those places have been given back.' : 'The place has been given back.');
      await load();
    } catch (err) {
      toast.error(err?.message || 'Could not give the place back');
    } finally {
      setBusyId(null);
    }
  };

  /**
   * Load the chosen player's records. NOTHING loads without a player — the point
   * of the screen is that it answers a question about somebody, and the list
   * nobody asked for is what made the last version unusable.
   */
  const load = useCallback(async () => {
    if (!academyId || !person) return;
    setLoading(true);
    try {
      setData(await paymentsAPI.academyOrders(academyId, {
        userId: person.id,
        status: STATUS[statusId].status,
        kind:   kind === 'all' ? undefined : kind,
        from:   from || undefined,
        to:     to   || undefined,
        limit:  50,
      }));
    } catch (err) {
      toast.error(err?.message || 'Could not load their records');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [academyId, person, statusId, kind, from, to]);

  useEffect(() => { load(); }, [load]);

  /**
   * The three ways a row can change, all through the same shape.
   *
   * Every one of them is refused by the server in some state — already paid,
   * already refunded, paid-so-cancel-is-wrong, no capability — and every one of
   * those refusals is worth reading rather than swallowing. "Already paid" in
   * particular means a colleague got there first, not that this failed.
   */
  const act = async (order, action) => {
    setBusy(order.id);
    const amount = money(order.total, order.currency);
    try {
      if (action === 'settle') {
        await paymentsAPI.markOrderPaid(order.id, {
          paymentMethod: method,
          reference: reference.trim() || undefined,
        });
        toast.success(
          `${amount} recorded as ${methodLabel(method)}`
          + (reference.trim() ? ` #${reference.trim()}` : '')
          + ' — receipt sent',
        );
      } else if (action === 'refund') {
        const res = await paymentsAPI.refundOrder(order.id, { reason: reason.trim() || undefined });
        toast.success(
          order.paymentMethod === 'stripe'
            ? `${amount} refunded to the card — it may take a few days to appear`
            : `${amount} recorded as refunded — hand the money back`,
        );
        // Surfaced because it is the proof the card was actually reversed rather
        // than just the row being relabelled.
        if (res?.stripeRefundId) console.log('[desk] stripe refund', res.stripeRefundId);
      } else {
        const res = await paymentsAPI.cancelOrder(order.id, { reason: reason.trim() || undefined });
        toast.success(
          res?.placesReleased
            ? `Cancelled — ${res.placesReleased} place${res.placesReleased === 1 ? '' : 's'} released`
            : 'Cancelled',
        );
      }
      closePanel();
      await load();
    } catch (err) {
      toast.error(err?.message || 'That did not go through');
    } finally {
      setBusy(null);
    }
  };

  // "Not booked" is a subset of settled rather than a status of its own, so it
  // narrows here — the server has no such state to ask for.
  const orders = useMemo(() => (data?.orders || [])
    .filter(o => !STATUS[statusId].unmatchedOnly || o.unmatched)
    .filter(o => !STATUS[statusId].orphanedOnly || o.orphaned)
    .filter(o => !onlyOrder || o.orderNumber === onlyOrder), [data, statusId, onlyOrder]);

  const input = `px-3 py-2 rounded-lg border ${theme.border} ${theme.card} ${theme.text} text-sm`;

  return (
    <div className="space-y-4">
      <div>
        <h2 className={`text-xl font-bold ${theme.text}`}>Front desk</h2>
        <p className={`text-sm ${theme.textSecondary} mt-0.5`}>
          Find the player in front of you, then take payment, refund it, or cancel
          what was never paid for.
        </p>
      </div>

      {/* ── Finding them ──────────────────────────────────────────────────────
          The search box stays put whether or not somebody is selected: it is how
          you move on to the next player in the queue, and a control that
          disappears once used is one you have to go hunting for again. */}
      <div className={`${theme.card} rounded-xl p-3 space-y-3`}>
        <div className="flex items-center gap-2">
          <div className="relative flex-1 min-w-0">
            <Search size={14} className={`absolute left-3 top-1/2 -translate-y-1/2 ${theme.textMuted}`} />
            {/* Not a form field the browser should remember.
                Renaming alone would not do it: Chrome decides what to offer from
                the name, id, label AND placeholder, and a placeholder reading
                "name, email or phone" is three separate invitations to autofill
                somebody's contact details over a staff lookup. Hence a name that
                matches none of its heuristics, autoComplete off, and the words
                email/phone moved out of the field and into the hint below.
                data-1p-ignore / data-lpignore do the same for password managers,
                which ignore autoComplete. */}
            <input
              value={term}
              onChange={e => setTerm(e.target.value)}
              onKeyDown={(e) => {
                if (!matches?.length) return;
                if (e.key === 'Enter')      { e.preventDefault(); choose(matches[Math.min(active, matches.length - 1)]); }
                else if (e.key === 'ArrowDown') { e.preventDefault(); setActive(i => Math.min(i + 1, matches.length - 1)); }
                else if (e.key === 'ArrowUp')   { e.preventDefault(); setActive(i => Math.max(i - 1, 0)); }
                else if (e.key === 'Escape')    { setMatches(null); setMatchedOrder(null); }
              }}
              type="search"
              name="deskPlayerLookup"
              id="deskPlayerLookup"
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
              data-1p-ignore
              data-lpignore="true"
              // Announced as what it is — a lookup that suggests as you type —
              // rather than as a text box that mysteriously grows a list.
              role="combobox"
              aria-expanded={matches !== null && term.trim().length >= 2}
              aria-autocomplete="list"
              aria-activedescendant={matches?.length ? `desk-person-opt-${active}` : undefined}
              placeholder="Who is at the counter?"
              data-testid="desk-person-search"
              className={`w-full ${input} pl-8 pr-8`}
            />
            {/* Inside the box, so its arrival cannot move anything — and it is
                the only thing that says a short term is still being looked up. */}
            {seeking && (
              <Loader2 size={14} className={`absolute right-3 top-1/2 -translate-y-1/2 animate-spin ${theme.textMuted}`} />
            )}

            {/* Overlaid rather than pushing the page down. */}
            {matches !== null && term.trim().length >= 2 && (
              <div className={`absolute z-20 mt-1 w-full ${theme.card} border ${theme.border} rounded-xl shadow-lg max-h-72 overflow-y-auto`}>
                {matchedOrder && (
                  <p className={`px-3 pt-2 text-xs ${theme.textSecondary}`}>
                    {matchedOrder} — showing who paid it
                  </p>
                )}
                {matches.length === 0 ? (
                  <p className={`px-3 py-3 text-sm ${theme.textMuted}`}>
                    No payment record matches that name or order number.
                  </p>
                ) : /* Suggestion ids are desk-suggestion-<id>, NOT desk-person-<id>:
                       the latter shares a prefix with the search box's own
                       desk-person-search, so a prefix selector matches the input
                       and a test clicking "the suggestion" silently clicks the
                       text field instead. */
                  matches.map((p, i) => (
                  <button
                    key={p.id}
                    onClick={() => choose(p)}
                    onMouseEnter={() => setActive(i)}
                    data-testid={`desk-suggestion-${p.id}`}
                    id={`desk-person-opt-${i}`}
                    role="option"
                    aria-selected={i === active}
                    className={`w-full text-left px-3 py-2 flex items-center justify-between gap-3 ${
                      i === active ? theme.backgroundSecondary : theme.cardHover
                    }`}
                  >
                    <span className="min-w-0">
                      <span className={`block text-sm font-medium ${theme.text} truncate`}>{p.name}</span>
                      {/* Everything that separates two of the same name, in the
                          order a receptionist would ask for it. A managed child
                          usually has no email at all, so the parent comes first. */}
                      <span className={`block text-xs ${theme.textMuted} truncate`}>
                        {[
                          p.managedBy && `managed by ${p.managedBy}`,
                          p.email,
                          p.phone,
                        ].filter(Boolean).join(' · ') || 'no contact details on file'}
                      </span>
                    </span>
                    <span className={`text-xs flex-shrink-0 tabular-nums ${p.owed > 0 ? 'text-amber-600 font-semibold' : theme.textMuted}`}>
                      {p.owed > 0 ? `owes ${money(p.owed)}` : 'nothing owed'}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Digits only, and numeric keypad on a phone — this gets typed at a
              counter while somebody reads a number aloud. Enter submits, because
              that is what anybody typing a reference will press. */}
          <div className="relative flex-shrink-0">
            <Hash size={13} className={`absolute left-2.5 top-1/2 -translate-y-1/2 ${theme.textMuted}`} />
            <input
              value={ref}
              onChange={e => { setRef(e.target.value); setRefMiss(false); }}
              onKeyDown={e => { if (e.key === 'Enter') findByRef(ref); }}
              onBlur={() => ref.trim() && findByRef(ref)}
              inputMode="numeric"
              type="search"
              name="deskOrderRef"
              id="deskOrderRef"
              autoComplete="off"
              data-1p-ignore
              data-lpignore="true"
              placeholder="Order number"
              aria-label="Order number from a receipt"
              data-testid="desk-order-ref"
              className={`${input} pl-7 w-40 ${refMiss ? 'border-red-500' : ''}`}
            />
            {refBusy && (
              <Loader2 size={13} className={`absolute right-2.5 top-1/2 -translate-y-1/2 animate-spin ${theme.textMuted}`} />
            )}
          </div>

          <button
            onClick={() => setScanning(true)}
            data-testid="desk-scan-qr"
            className={`${btn.base} ${btn.sm} ${theme.buttonGhost} inline-flex items-center gap-1.5 flex-shrink-0`}
          >
            <ScanLine size={14} /> Scan
          </button>
        </div>

        <p className={`text-xs ${theme.textMuted}`}>
          {refMiss
            ? <span className="text-red-600">No order here matches &ldquo;{ref}&rdquo;. Check the digits, or search by name.</span>
            : <>Search by name, email or phone — every word has to match, so
              &ldquo;smith 4821&rdquo; finds the Smith on that number. Or type the
              digits from a receipt: 2608230001, or the whole
              US-KSA-TRN-2608230001.</>}
        </p>

        {/* Who is selected, and the one figure a desk needs: what THEY owe. In a
            fixed-height strip, so choosing somebody does not shunt the list down
            the page. */}
        <div className={`min-h-[2.75rem] flex items-center gap-3 rounded-lg px-3 py-2 ${
          person ? theme.backgroundSecondary : ''
        }`}>
          {person ? (
            <>
              <div className="min-w-0 flex-1">
                <p className={`text-sm font-semibold ${theme.text} truncate`}>
                  {person.name}
                  <span className={`font-normal ${theme.textMuted}`}>
                    {person.managedBy ? ` · managed by ${person.managedBy}` : ''}
                    {person.email ? ` · ${person.email}` : ''}
                  </span>
                </p>
                {onlyOrder && (
                  <p className="text-xs mt-0.5">
                    <span className={theme.textSecondary}>Showing order {onlyOrder} only</span>
                    <button
                      onClick={() => { setOnlyOrder(null); setRef(''); }}
                      data-testid="desk-show-all-records"
                      className={`ml-2 ${theme.link}`}
                    >
                      show all their records
                    </button>
                  </p>
                )}
                <p className={`text-xs ${person.owed > 0 ? 'text-amber-600 font-medium' : theme.textMuted}`}>
                  {person.owed > 0
                    ? `Owes ${money(person.owed)} across ${person.owedCount} order${person.owedCount === 1 ? '' : 's'}`
                    : 'Nothing outstanding as the payer'}
                  {person.playedCount > 0 && ` · named on ${person.playedCount} booking${person.playedCount === 1 ? '' : 's'}`}
                </p>
              </div>
              <button
                onClick={clearPerson}
                data-testid="desk-clear-person"
                className={`text-xs ${theme.textMuted} inline-flex items-center gap-1 flex-shrink-0`}
              >
                <X size={12} /> Clear
              </button>
            </>
          ) : (
            <p className={`text-xs ${theme.textMuted}`}>Nobody selected.</p>
          )}
        </div>

        {/* Narrowing: always present, always the same size, disabled until there
            is somebody to narrow rather than appearing once there is. */}
        <div className="flex items-center gap-2 flex-wrap">
          <select
            value={kind}
            onChange={e => { setKind(e.target.value); closePanel(); }}
            disabled={!person}
            data-testid="desk-offering-filter"
            aria-label="Which offering"
            className={`${input} w-40 ${person ? '' : 'opacity-40'}`}
          >
            {OFFERINGS.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
          </select>
          <select
            value={statusId}
            onChange={e => { setStatusId(e.target.value); closePanel(); }}
            disabled={!person}
            data-testid="desk-status-filter"
            aria-label="Which records"
            className={`${input} w-40 ${person ? '' : 'opacity-40'}`}
          >
            {STATUSES.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
          </select>
          {/* Shortcuts, then the dates themselves. Native date inputs, so the
              calendar is the platform's own — and setting both to the same day
              is how you ask for one day's takings. */}
          <div className={`inline-flex rounded-lg border ${theme.border} p-0.5`}>
            {SHORTCUTS.map(sc => {
              const r = sc.range();
              const active = r.from === from && r.to === to;
              return (
                <button
                  key={sc.id}
                  onClick={() => { setFrom(r.from); setTo(r.to); closePanel(); }}
                  disabled={!person}
                  data-testid={`desk-range-${sc.id}`}
                  className={`px-2 py-1 rounded-md text-xs font-medium whitespace-nowrap transition-colors ${
                    active ? theme.tabActive : `${theme.textMuted} ${theme.cardHover}`
                  } ${person ? '' : 'opacity-40'}`}
                >
                  {sc.label}
                </button>
              );
            })}
          </div>
          <div className="flex items-center gap-1">
            <input
              type="date"
              value={from}
              max={to || undefined}
              onChange={e => { setFrom(e.target.value); closePanel(); }}
              disabled={!person}
              data-testid="desk-from"
              aria-label="From date"
              className={`${input} ${person ? '' : 'opacity-40'}`}
            />
            <span className={`text-xs ${theme.textMuted}`}>to</span>
            <input
              type="date"
              value={to}
              min={from || undefined}
              onChange={e => { setTo(e.target.value); closePanel(); }}
              disabled={!person}
              data-testid="desk-to"
              aria-label="To date"
              className={`${input} ${person ? '' : 'opacity-40'}`}
            />
          </div>
          <p className={`text-xs ${theme.textMuted}`}>
            {!person ? 'Search for a player to load their payments.'
              : loading ? 'Loading…'
              : `${orders.length} record${orders.length === 1 ? '' : 's'}`}
            {/* A page presented without saying it is a page turns every figure on
                the screen into a claim about everything. */}
            {data?.truncated && (
              <span className="text-amber-600">
                {` · newest ${data.shownCount} of ${data.matchingCount} — narrow the dates to see the rest`}
              </span>
            )}
          </p>
        </div>
      </div>

      {/* Column headings, on desktop only, sharing the row geometry literally. */}
      {person && orders.length > 0 && (
        <div className={`hidden md:grid ${COLS} px-4 text-[10px] uppercase tracking-wide ${theme.textMuted}`}>
          <span>What they bought</span>
          <span>Payment type</span>
          <span className="text-right">Amount</span>
          <span className="text-right">Action</span>
        </div>
      )}

      {/* The list dims while reloading instead of being replaced by a spinner:
          the rows keep their positions, so the row you were reaching for is
          still under your finger when the data comes back. */}
      <div className={`relative min-h-[12rem] transition-opacity ${loading && data ? 'opacity-40' : ''}`}>
        {!person ? (
          <div className={`text-center py-16 ${theme.textMuted}`}>
            <UserSearch size={28} className="mx-auto mb-3 opacity-40" />
            <p className={`text-sm font-medium ${theme.textSecondary}`}>
              Nothing is loaded until you pick a player.
            </p>
            <p className="text-xs mt-1">
              Search by name, email or phone — or scan the QR code on their profile.
            </p>
          </div>
        ) : loading && !data ? (
          <div className={`flex items-center justify-center gap-2 py-20 ${theme.textMuted}`}>
            <Loader2 size={18} className="animate-spin" /> <span className="text-sm">Loading…</span>
          </div>
        ) : orders.length === 0 ? (
          <div className={`text-center py-16 ${theme.textMuted}`}>
            <Check size={26} className="mx-auto mb-2 opacity-40" />
            <p className="text-sm">
              {onlyOrder ? `Order ${onlyOrder} is not among their records — it may belong to another academy.`
                : kind !== 'all' ? `Nothing under ${OFFERINGS.find(o => o.id === kind).label.toLowerCase()} in this period.`
                : statusId === 'pending' ? `${person.name} owes nothing in this period.`
                : statusId === 'notbooked' ? 'None of their money is sitting against something unbooked.'
                : statusId === 'refunded' ? 'Nothing has been refunded to them.'
                : (!from && !to) ? 'No payment records for them at all.'
                : from && from === to ? `Nothing on ${from}.`
                : 'Nothing in this period — try a wider date range.'}
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {orders.map(o => {
              const open = panel?.id === o.id ? panel.action : null;
              return (
                <div
                  key={o.id}
                  className={`${theme.card} rounded-xl p-4 ${
                    onlyOrder && o.orderNumber === onlyOrder
                      ? 'ring-2 ring-current'
                      : ''
                  }`}
                >
                  <div className={COLS}>
                    {/* ── What, and who paid ─────────────────────────────────
                        The player is already named at the top of the screen, so
                        the row leads with what was bought. The payer is called
                        out only when it is somebody else — the case that catches
                        people out, and the reason a child's name has to find an
                        order in their parent's name. */}
                    <div className="min-w-0">
                      <ul className="space-y-0.5">
                        {(o.items || []).map(i => (
                          <li key={i.id} className={`text-sm ${theme.text}`}>
                            {i.label}
                            {/* Who this line is for, when the order covers more
                                than one. Two entries in one tournament print the
                                same label twice, and without the name the desk
                                cannot tell two children from one duplicate. */}
                            {(o.items || []).length > 1 && i.playerName && (
                              <span className={theme.textSecondary}> · {i.playerName}</span>
                            )}
                            {/* A weekly session names every occurrence the same,
                                so without the date two rows are the same row. */}
                            {i.startsAt && (
                              <span className={`text-xs ${theme.textMuted}`}> · {when(i.startsAt)}</span>
                            )}
                          </li>
                        ))}
                      </ul>
                      <p className={`text-xs ${theme.textMuted} mt-1`}>
                        {/* The reference on their receipt. First on the line
                            because when somebody is holding one, matching it is
                            the whole task. */}
                        {o.orderNumber && (
                          <span className={`font-medium ${theme.textSecondary}`}>{o.orderNumber} · </span>
                        )}
                        {o.payer?.name && o.payer.name !== person.name && (
                          <span className={theme.textSecondary}>Paid by {o.payer.name} · </span>
                        )}
                        Booked {when(o.createdAt)}
                        {o.confirmedAt ? ` · settled ${when(o.confirmedAt)}` : ''}
                      </p>
                    </div>

                    {/* ── Payment type ───────────────────────────────────────
                        Its own column rather than a word in the timeline:
                        reconciling a till means scanning one place down the list
                        for how each payment arrived. Muted when nothing has
                        settled — "Not recorded" is a state, not a method. */}
                    <div className="mt-2 md:mt-0 min-w-0">
                      <p className={`md:hidden text-[10px] uppercase tracking-wide ${theme.textMuted}`}>
                        Payment type
                      </p>
                      <p
                        data-testid={`pay-type-${o.id}`}
                        className={`text-sm truncate ${o.paymentMethod ? `font-medium ${theme.text}` : theme.textMuted}`}
                      >
                        {methodLabel(o.paymentMethod)}
                      </p>
                      {/* The number under the method, which is how a check is
                          matched against a bank statement. */}
                      {o.paymentReference && (
                        <p className={`text-[11px] ${theme.textMuted} truncate`}>#{o.paymentReference}</p>
                      )}
                    </div>

                    {/* ── Amount and status ──────────────────────────────── */}
                    <div className="mt-2 md:mt-0 md:text-right">
                      <p className={`text-lg font-semibold ${theme.text} tabular-nums`}>
                        {money(o.total, o.currency)}
                      </p>
                      <span className={`text-[11px] px-2 py-0.5 rounded-full ${
                        o.status === 'paid' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'
                          : o.status === 'pending' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'
                          : `${theme.backgroundSecondary} ${theme.textMuted}`
                      }`}>
                        {o.status}
                      </span>
                    </div>

                    {/* ── Action ─────────────────────────────────────────────
                        Always in this column, and never removed when the panel
                        below opens: a row that swaps out the button you just
                        pressed moves everything under it. */}
                    <div className="mt-3 md:mt-0 flex md:flex-col md:items-end gap-2">
                      {o.status === 'pending' && (
                        <>
                          <button
                            onClick={() => (open === 'settle' ? closePanel() : openPanel(o.id, 'settle'))}
                            data-testid={`take-payment-${o.id}`}
                            className={`${btn.base} ${btn.sm} inline-flex items-center gap-1.5 whitespace-nowrap ${
                              open === 'settle'
                                ? theme.buttonPrimary
                                : `${theme.borderAccent} border ${theme.link} ${theme.cardHover} `
                            }`}
                          >
                            <Banknote size={13} /> Take payment
                          </button>
                          <button
                            onClick={() => (open === 'cancel' ? closePanel() : openPanel(o.id, 'cancel'))}
                            data-testid={`cancel-order-${o.id}`}
                            className={`text-xs ${open === 'cancel' ? 'text-red-600 dark:text-red-400 font-medium' : theme.textMuted} hover:text-red-600 dark:hover:text-red-400`}
                          >
                            Cancel order
                          </button>
                        </>
                      )}
                      {/* An entry standing with no live charge. Two ways out,
                          and the desk has to pick one: collect for the place,
                          or take the place back. Offered together because
                          either is right and neither is obvious. */}
                      {o.orphaned && (
                        <>
                          <button
                            onClick={() => rebill(o)}
                            disabled={busyId === o.id}
                            data-testid={`rebill-order-${o.id}`}
                            className={`${btn.base} ${btn.sm} inline-flex items-center gap-1.5 whitespace-nowrap ${theme.borderAccent} border ${theme.link} ${theme.cardHover}  disabled:opacity-50`}
                          >
                            <Banknote size={13} /> Bill again
                          </button>
                          <button
                            onClick={() => releaseEntry(o)}
                            disabled={busyId === o.id}
                            data-testid={`release-entry-${o.id}`}
                            className={`text-xs ${theme.textMuted} hover:text-red-600 dark:hover:text-red-400 disabled:opacity-50`}
                          >
                            Unregister
                          </button>
                        </>
                      )}
                      {o.status === 'paid' && (
                        <button
                          onClick={() => (open === 'refund' ? closePanel() : openPanel(o.id, 'refund'))}
                          data-testid={`refund-order-${o.id}`}
                          className={`${btn.base} ${btn.sm} inline-flex items-center gap-1.5 whitespace-nowrap ${
                            open === 'refund'
                              ? 'bg-amber-600 text-white'
                              : `border ${theme.border} ${theme.textSecondary} hover:text-amber-600 dark:hover:text-amber-400`
                          }`}
                        >
                          <Undo2 size={12} /> Refund
                        </button>
                      )}
                    </div>
                  </div>

                  {/* ── The flags, in reserved space ──────────────────────────
                      Money held against nothing, who gave the place up, who gave
                      the money back. Reserved height, so a row is the same height
                      whether or not any of them apply. */}
                  <div className="mt-1.5 min-h-[1.1rem] text-[11px] flex flex-wrap items-center gap-x-3 gap-y-1">
                    {o.unmatched && (
                      <span
                        className={o.unmatched.stranded ? 'text-red-600 font-semibold' : 'text-amber-600'}
                        title={o.unmatched.stranded
                          ? `The date has passed, so this can never be used — refund by ${o.unmatched.refundBy} or keep it`
                          : `Credit against something still to come. If refunded, it must go back by ${o.unmatched.refundBy}`}
                      >
                        {money(o.unmatched.amount, o.currency)}
                        {o.unmatched.stranded ? ' stranded — the date has passed' : ' not booked'}
                        {` · returnable by ${o.unmatched.refundBy}`}
                      </span>
                    )}
                    {o.orphaned && (
                      <span
                        className="text-red-600 font-semibold"
                        title={'The charge was pulled and the place was never given up. Nothing can be '
                             + 'collected against it as it stands: bill it again, or take the place back.'}
                      >
                        {money(o.orphaned.amount, o.currency)} owed — still enrolled
                      </span>
                    )}
                    {o.released && (
                      <span className="text-amber-600">
                        {o.released.action} {when(o.released.at)}
                        {o.released.by ? ` by ${o.released.by}` : ''}
                      </span>
                    )}
                    {o.refunded && (
                      <span className="text-amber-700 dark:text-amber-400">
                        refunded {when(o.refunded.at)}
                        {o.refunded.by ? ` by ${o.refunded.by}` : ''}
                        {o.refunded.amount ? ` · ${money(o.refunded.amount, o.currency)}` : ''}
                        {/* The Stripe id is the proof the card was really
                            reversed rather than the row being relabelled. */}
                        {o.refunded.stripeRefundId ? ' · back to the card' : ''}
                      </span>
                    )}
                  </div>

                  {/* ── Taking the money ──────────────────────────────────── */}
                  {o.status === 'pending' && open === 'settle' && (
                    <div className={`mt-3 pt-3 border-t ${theme.border}`}>
                      {/* Naming the recipient matters: the receipt goes to the
                          payer, who may not be the player standing at the desk.
                          A parent gets the email for their child's session. */}
                      <p className={`text-xs ${theme.textMuted} mb-2`}>
                        How did they pay? This records the money as received and
                        emails a receipt to {o.payer?.name || 'the payer'}.
                      </p>
                      <div className="flex flex-wrap gap-1.5 mb-3">
                        {METHODS.map(m => {
                          const Icon = m.icon;
                          return (
                            <button
                              key={m.id}
                              onClick={() => { setMethod(m.id); setReference(''); }}
                              data-testid={`pay-method-${m.id}`}
                              className={`px-2.5 py-1.5 rounded-md text-xs font-medium border inline-flex items-center gap-1.5 transition-colors ${
                                method === m.id ? `${theme.tabActive} border-transparent`
                                  : `${theme.border} ${theme.textSecondary} ${theme.cardHover}`
                              }`}
                            >
                              <Icon size={12} /> {m.label}
                            </button>
                          );
                        })}
                      </div>

                      {/* Shown only for the methods that leave something to quote
                          back. Cash has no number, and an empty field beside it
                          would just be one more thing to skip past. */}
                      {METHOD[method]?.refLabel && (
                        <div className="mb-3">
                          <input
                            value={reference}
                            onChange={e => setReference(e.target.value)}
                            placeholder={METHOD[method].refLabel}
                            maxLength={100}
                            data-testid={`pay-reference-${o.id}`}
                            className={`w-full sm:w-56 ${input}`}
                          />
                          {METHOD[method].needsRef && (
                            <p className={`text-[11px] ${theme.textMuted} mt-1`}>
                              Needed so the payment can be traced if the check bounces
                              or the payer says they already paid.
                            </p>
                          )}
                        </div>
                      )}

                      <div className="flex gap-2">
                        <button onClick={closePanel} className={`${btn.base} ${btn.sm} ${theme.buttonGhost}`}>
                          Back
                        </button>
                        <button
                          onClick={() => act(o, 'settle')}
                          // Blocked here as well as refused by the server: being
                          // told after the click that a required field was empty
                          // is worse than the button saying so first.
                          disabled={busy === o.id || (METHOD[method]?.needsRef && !reference.trim())}
                          data-testid={`confirm-order-${o.id}`}
                          className={`${btn.base} ${btn.sm} ${theme.buttonPrimary} flex items-center gap-1.5`}
                        >
                          {busy === o.id ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />}
                          Record {money(o.total, o.currency)} as {methodLabel(method)}
                          {reference.trim() ? ` #${reference.trim()}` : ''}
                        </button>
                      </div>
                    </div>
                  )}

                  {/* ── Giving it back ────────────────────────────────────────
                      Says plainly that the place is not released. The two are
                      separate decisions, and a refund that quietly threw someone
                      out of a session would be the worse surprise. */}
                  {o.status === 'paid' && open === 'refund' && (
                    <div className={`mt-3 pt-3 border-t ${theme.border}`}>
                      <p className={`text-xs ${theme.textMuted} mb-2`}>
                        Refund {money(o.total, o.currency)} in full to {o.payer?.name || 'the payer'}
                        {o.paymentMethod === 'stripe'
                          ? ' — this reverses the card charge at Stripe, and the money can only go back to that card.'
                          : ` — record this once you have handed the ${methodLabel(o.paymentMethod).toLowerCase()} back.`}
                        {' '}The place stays booked; cancel it separately if they are not playing.
                      </p>
                      <input
                        value={reason}
                        onChange={e => setReason(e.target.value)}
                        placeholder="Reason (optional, kept on the record)"
                        className={`w-full ${input} mb-3`}
                      />
                      <div className="flex gap-2">
                        <button onClick={closePanel} className={`${btn.base} ${btn.sm} ${theme.buttonGhost}`}>
                          Back
                        </button>
                        <button
                          onClick={() => act(o, 'refund')}
                          disabled={busy === o.id}
                          data-testid={`confirm-refund-${o.id}`}
                          className={`${btn.base} ${btn.sm} bg-amber-600 text-white hover:bg-amber-700 flex items-center gap-1.5`}
                        >
                          {busy === o.id ? <Loader2 size={13} className="animate-spin" /> : <Undo2 size={13} />}
                          Refund {money(o.total, o.currency)}
                        </button>
                      </div>
                    </div>
                  )}

                  {/* ── Dropping an unpaid one ───────────────────────────────── */}
                  {o.status === 'pending' && open === 'cancel' && (
                    <div className={`mt-3 pt-3 border-t ${theme.border}`}>
                      <p className={`text-xs ${theme.textMuted} mb-2`}>
                        Withdraw this unpaid order and give the place back. Nothing
                        was collected, so there is nothing to refund.
                      </p>
                      <input
                        value={reason}
                        onChange={e => setReason(e.target.value)}
                        placeholder="Reason (optional, kept on the record)"
                        className={`w-full ${input} mb-3`}
                      />
                      <div className="flex gap-2">
                        <button onClick={closePanel} className={`${btn.base} ${btn.sm} ${theme.buttonGhost}`}>
                          Back
                        </button>
                        <button
                          onClick={() => act(o, 'cancel')}
                          disabled={busy === o.id}
                          data-testid={`confirm-cancel-${o.id}`}
                          className={`${btn.base} ${btn.sm} ${theme.buttonDanger} flex items-center gap-1.5`}
                        >
                          {busy === o.id ? <Loader2 size={13} className="animate-spin" /> : <XCircle size={13} />}
                          Cancel this order
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <p className={`text-xs ${theme.textMuted} flex items-start gap-1.5`}>
        <AlertTriangle size={12} className="mt-0.5 flex-shrink-0" />
        Recording a payment is who the money is credited to and what a payout is
        later worked out from, and it emails the payer a receipt. To correct one,
        refund it and take the right amount again — every step stays on the record.
      </p>

      <DeskScanModal
        isOpen={scanning}
        onClose={() => setScanning(false)}
        onScan={onScan}
      />
    </div>
  );
};

export default PaymentDeskSettings;
